import { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Filter, X } from "lucide-react";
import { LOGO, BRAND_NAME } from "@/lib/brand";
import { BlogHeader } from "@/components/Blog/BlogHeader";
import { BlogMasonryGrid } from "@/components/Blog/BlogMasonryGrid";
import { BlogCard } from "@/components/Blog/BlogCard";
import { Button } from "@/components/ui/button";
import { Footer } from "@/components/Footer";
import {
  getAllPosts,
  getCategoriesFromPosts,
  mergePosts,
  searchPosts,
  BlogPost,
  BlogCategory,
} from "@/data/blogData";
import { fetchGeneratedPosts } from "@/services/blogService";
import { SEO } from "@/components/SEO";
import { isSubdomain } from "@/lib/subdomain";

interface BlogListPageProps {
  onMobileMenuOpen?: () => void;
}

export function BlogListPage({ onMobileMenuOpen }: BlogListPageProps) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [categories, setCategories] = useState<BlogCategory[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  // Initialize data (static evergreen + ephemeral AI posts)
  useEffect(() => {
    let cancelled = false;

    async function loadPosts() {
      const generated = await fetchGeneratedPosts();
      const merged = mergePosts(getAllPosts(), generated);
      if (cancelled) return;
      setPosts(merged);
      setCategories(getCategoriesFromPosts(merged));
    }

    void loadPosts();

    const categoryParam = searchParams.get("category");
    const searchParam = searchParams.get("search");

    if (categoryParam) setSelectedCategory(categoryParam);
    if (searchParam) setSearchQuery(searchParam);

    return () => {
      cancelled = true;
    };
  }, [searchParams]);

  // Filter posts
  const filteredPosts = useMemo(() => {
    let result = posts;

    // Apply search filter
    if (searchQuery) {
      result = searchPosts(searchQuery, posts);
    }

    // Apply category filter
    if (selectedCategory) {
      result = result.filter(
        (post) =>
          post.category.toLowerCase() === selectedCategory.toLowerCase(),
      );
    }

    return result;
  }, [posts, searchQuery, selectedCategory]);

  // Featured post (first post that's featured)
  const featuredPost = useMemo(() => {
    if (searchQuery || selectedCategory) return null;
    return posts.find((post) => post.featured);
  }, [posts, searchQuery, selectedCategory]);

  // Remaining posts for masonry grid
  const gridPosts = useMemo(() => {
    if (searchQuery || selectedCategory) return filteredPosts;
    return filteredPosts.filter((post) => post.id !== featuredPost?.id);
  }, [filteredPosts, featuredPost, searchQuery, selectedCategory]);

  const handlePostClick = (slug: string) => {
    // On blog subdomain, posts are at /:slug, otherwise /blog/:slug
    if (isSubdomain("blog")) {
      navigate(`/${slug}`);
    } else {
      navigate(`/blog/${slug}`);
    }
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    if (query) {
      setSearchParams({ search: query });
    } else {
      searchParams.delete("search");
      setSearchParams(searchParams);
    }
  };

  const handleCategorySelect = (categorySlug: string | null) => {
    setSelectedCategory(categorySlug);
    if (categorySlug) {
      setSearchParams({ category: categorySlug });
    } else {
      searchParams.delete("category");
      setSearchParams(searchParams);
    }
    setIsFilterOpen(false);
  };

  const clearFilters = () => {
    setSearchQuery("");
    setSelectedCategory(null);
    setSearchParams({});
  };

  const hasActiveFilters = searchQuery || selectedCategory;

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Blog | DevDotCom"
        description="Insights, tutorials, and updates from the DevDotCom team. Explore articles on web development, AI, cloud architecture, and more."
        canonical="/blog"
      />

      <BlogHeader onSearch={handleSearch} onMobileMenuOpen={onMobileMenuOpen} />

      {/* Hero Section */}
      <section className="pt-24 pb-12 md:pt-32 md:pb-16 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-gradient-to-br from-muted/50 to-transparent rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-gradient-to-tl from-muted/30 to-transparent rounded-full blur-3xl" />
        </div>

        <div className="container mx-auto px-6 lg:px-12 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <div className="inline-flex items-center justify-center w-14 h-14 mb-6 border border-border rounded-xl bg-background/50 backdrop-blur-sm p-2">
              <img
                src={LOGO.withoutBg}
                alt={BRAND_NAME}
                className="h-full w-full object-contain dark:hidden"
                width={40}
                height={40}
                loading="eager"
                decoding="async"
              />
              <img
                src={LOGO.dark}
                alt=""
                aria-hidden="true"
                className="h-full w-full object-contain hidden dark:block"
                width={40}
                height={40}
                loading="eager"
                decoding="async"
              />
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-4">
              DevDotCom Blog
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground">
              Insights, tutorials, and updates from our team of engineers and
              designers
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filter Bar — stays under the always-visible header */}
      <section className="sticky top-14 z-40 border-b border-border bg-background/95 py-6 backdrop-blur-sm supports-[backdrop-filter]:bg-background/80">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between gap-4">
            {/* Category Pills - Desktop */}
            <div className="hidden md:flex items-center gap-2 flex-wrap">
              <button
                onClick={() => handleCategorySelect(null)}
                className={`px-4 py-2 text-sm font-medium rounded-full transition-all ${
                  !selectedCategory
                    ? "bg-foreground text-background"
                    : "bg-muted hover:bg-muted/80 text-foreground"
                }`}
              >
                All Posts
              </button>
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => handleCategorySelect(category.slug)}
                  className={`px-4 py-2 text-sm font-medium rounded-full transition-all ${
                    selectedCategory === category.slug
                      ? "bg-foreground text-background"
                      : "bg-muted hover:bg-muted/80 text-foreground"
                  }`}
                >
                  {category.name}
                  <span className="ml-1.5 text-xs opacity-60">
                    ({category.count})
                  </span>
                </button>
              ))}
            </div>

            {/* Mobile Filter Button */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsFilterOpen(!isFilterOpen)}
              className="md:hidden"
            >
              <Filter className="w-4 h-4 mr-2" />
              Filter
              {hasActiveFilters && (
                <span className="ml-2 w-2 h-2 bg-foreground rounded-full" />
              )}
            </Button>

            {/* Clear Filters */}
            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="text-muted-foreground"
              >
                <X className="w-4 h-4 mr-1" />
                Clear
              </Button>
            )}

            {/* Post Count */}
            <p className="text-sm text-muted-foreground">
              {filteredPosts.length}{" "}
              {filteredPosts.length === 1 ? "article" : "articles"}
            </p>
          </div>

          {/* Mobile Filter Dropdown */}
          <AnimatePresence>
            {isFilterOpen && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="md:hidden overflow-hidden"
              >
                <div className="flex flex-wrap gap-2 pt-4">
                  <button
                    onClick={() => handleCategorySelect(null)}
                    className={`px-3 py-1.5 text-sm font-medium rounded-full transition-all ${
                      !selectedCategory
                        ? "bg-foreground text-background"
                        : "bg-muted text-foreground"
                    }`}
                  >
                    All
                  </button>
                  {categories.map((category) => (
                    <button
                      key={category.id}
                      onClick={() => handleCategorySelect(category.slug)}
                      className={`px-3 py-1.5 text-sm font-medium rounded-full transition-all ${
                        selectedCategory === category.slug
                          ? "bg-foreground text-background"
                          : "bg-muted text-foreground"
                      }`}
                    >
                      {category.name}
                    </button>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>

      {/* Main Content */}
      <main className="py-12 md:py-16">
        <div className="container mx-auto px-6 lg:px-12">
          {filteredPosts.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-20"
            >
              <div className="inline-flex items-center justify-center w-16 h-16 mb-6 border border-border rounded-xl bg-muted/50 p-2">
                <img
                  src={LOGO.withoutBg}
                  alt={BRAND_NAME}
                  className="h-full w-full object-contain dark:hidden"
                  width={48}
                  height={48}
                  loading="lazy"
                  decoding="async"
                />
                <img
                  src={LOGO.dark}
                  alt=""
                  aria-hidden="true"
                  className="h-full w-full object-contain hidden dark:block"
                  width={48}
                  height={48}
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <h2 className="text-2xl font-bold mb-2">No articles found</h2>
              <p className="text-muted-foreground mb-6">
                Try adjusting your search or filter to find what you're looking
                for.
              </p>
              <Button onClick={clearFilters}>Clear Filters</Button>
            </motion.div>
          ) : (
            <>
              {/* Featured Post */}
              {featuredPost && !hasActiveFilters && (
                <motion.section
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  className="mb-12 md:mb-16"
                >
                  <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-6">
                    Featured Article
                  </h2>
                  <div className="max-w-4xl">
                    <BlogCard
                      post={featuredPost}
                      variant="featured"
                      onClick={() => handlePostClick(featuredPost.slug)}
                      className="md:flex md:gap-8"
                    />
                  </div>
                </motion.section>
              )}

              {/* All Posts Grid */}
              <section>
                {!hasActiveFilters && featuredPost && (
                  <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-6">
                    Latest Articles
                  </h2>
                )}
                <BlogMasonryGrid
                  posts={gridPosts}
                  onPostClick={handlePostClick}
                />
              </section>
            </>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
